import type { FC, PropsWithChildren } from 'react';
import { useMemo } from 'react';
import type { AltDealStructureGridCell } from '@makemydeal/dr-platform-types';
import type { Slot } from '../PaymentStructureGrid/types';
import { ActionsContext } from './ActionsContext';
import type { GridSelectionState } from './ActionsContext';
import { DataContext } from './DataContext';
import { MetaContext, computeGridMeta } from './MetaContext';

interface AltDealStructureDataProviderProps {
    slots: Slot[];
    terms: number[];
    setTerms: (terms: number[]) => void;
    downPayments: number[];
    setDownPayments: (downPayments: number[]) => void;
    retry: (batchKey: string) => void;
    onCellSelect: (cell: AltDealStructureGridCell) => void;
    selectedCells: GridSelectionState;
    isCellDisabled: (cell: AltDealStructureGridCell) => boolean;
    canCreateScenarios: boolean;
    handleCreateScenarios: () => void;
    isAtMaxScenarios: boolean;
    resetSelection: () => void;
    markEditedDownPayment: (dealType: 'finance' | 'lease', value: number) => void;
    editedDownPaymentKeys: Set<string>;
}

export const AltDealStructureDataProvider: FC<PropsWithChildren<AltDealStructureDataProviderProps>> = ({
    slots,
    terms,
    setTerms,
    downPayments,
    setDownPayments,
    retry,
    onCellSelect,
    selectedCells,
    isCellDisabled,
    canCreateScenarios,
    handleCreateScenarios,
    isAtMaxScenarios,
    resetSelection,
    markEditedDownPayment,
    editedDownPaymentKeys,
    children
}) => {
    const dataValue = useMemo(() => ({ slots, terms, downPayments }), [slots, terms, downPayments]);
    const metaValue = useMemo(() => computeGridMeta(slots), [slots]);
    const actionsValue = useMemo(
        () => ({
            retry,
            setTerms,
            setDownPayments,
            onCellSelect,
            selectedCells,
            isCellDisabled,
            canCreateScenarios,
            handleCreateScenarios,
            isAtMaxScenarios,
            resetSelection,
            markEditedDownPayment,
            editedDownPaymentKeys
        }),
        [
            retry,
            setTerms,
            setDownPayments,
            onCellSelect,
            selectedCells,
            isCellDisabled,
            canCreateScenarios,
            handleCreateScenarios,
            isAtMaxScenarios,
            resetSelection,
            markEditedDownPayment,
            editedDownPaymentKeys
        ]
    );

    return (
        <DataContext.Provider value={dataValue}>
            <MetaContext.Provider value={metaValue}>
                <ActionsContext.Provider value={actionsValue}>{children}</ActionsContext.Provider>
            </MetaContext.Provider>
        </DataContext.Provider>
    );
};
